import React, { useState, useRef, useEffect } from "react";
import api from "./../../../const/axios";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { connect } from "react-redux";
import { loadingOn, loadingOff } from "./../../../redux/actions";
import { withTranslation } from "react-i18next";
import { Search as SearchIcon } from "lucide-react";
import { cn } from "@/lib/utils";

// ─── Indeterminate checkbox ───────────────────────────────────────────────────

interface CheckItemProps {
  label: string;
  checked: boolean;
  indeterminate?: boolean;
  bold?: boolean;
  onChange: (checked: boolean) => void;
}

const CheckItem: React.FC<CheckItemProps> = ({ label, checked, indeterminate, bold, onChange }) => {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = !!indeterminate;
  }, [indeterminate]);

  return (
    <label className="flex items-center gap-2 cursor-pointer select-none group">
      <input
        ref={ref}
        type="checkbox"
        checked={checked}
        onChange={e => onChange(e.target.checked)}
        className="w-4 h-4 rounded border-gray-300 dark:border-gray-600 accent-blue-600 cursor-pointer flex-shrink-0"
      />
      <span className={cn(
        "text-sm text-gray-700 dark:text-gray-200 group-hover:text-gray-900 dark:group-hover:text-gray-100",
        bold && "font-semibold text-gray-800 dark:text-gray-100"
      )}>
        {label}
      </span>
    </label>
  );
};

// ─── Toggle switch ────────────────────────────────────────────────────────────

interface ToggleProps {
  checked: boolean;
  labelOn: string;
  labelOff: string;
  onChange: () => void;
}

const Toggle: React.FC<ToggleProps> = ({ checked, labelOn, labelOff, onChange }) => (
  <div className="flex items-center gap-3">
    <button
      type="button"
      onClick={onChange}
      className={cn(
        "relative inline-flex h-5 w-9 flex-shrink-0 items-center rounded-full transition-colors duration-200 focus:outline-none",
        checked ? "bg-blue-600" : "bg-gray-200"
      )}
    >
      <span className={cn(
        "inline-block h-3.5 w-3.5 rounded-full bg-white dark:bg-gray-900 shadow transition-transform duration-200",
        checked ? "translate-x-[18px]" : "translate-x-[3px]"
      )} />
    </button>
    <span className="text-sm text-gray-700 dark:text-gray-200">{checked ? labelOn : labelOff}</span>
  </div>
);

// ─── Main component ───────────────────────────────────────────────────────────

interface QuestionSearchProps {
  setElements: (data: any[], showAnswers: boolean, keywords: string[]) => void;
  loadingOn: () => void;
  loadingOff: () => void;
  t: (key: string) => string;
}

const QuestionSearchComponent: React.FC<QuestionSearchProps> = ({
  setElements,
  loadingOn,
  loadingOff,
  t,
}) => {
  const [checkedList, setCheckedList] = useState<number[]>([0, 1, 2, 3, 4, 5]);
  const [checkedListPlaces, setCheckedListPlaces] = useState<number[]>([0, 1, 2, 3]);
  const [count, setCount] = useState(10);
  const [showAnswers, setShowAnswers] = useState(false);
  const [keyword, setKeyword] = useState("");
  const [from, setFrom] = useState("2001-01-01");
  const [to, setTo] = useState("2030-01-01");
  const [error, setError] = useState<string | null>(null);

  const gameTypes = [
    t("search.whatWhereWhen"),
    t("search.khamsa"),
    t("search.individualGame"),
    t("search.brainRing"),
    t("search.eruditQuartet"),
    t("search.osip"),
  ];

  const searchPlaces = [
    t("search.questionText"),
    t("search.answerText"),
    t("search.commentText"),
    t("search.requisiteText"),
  ];

  const searchQuestion = async () => {
    if (keyword.trim().length < 3) {
      setError(t("search.enterKeyword"));
      return;
    }
    setError(null);
    const keywords = [keyword.trim()];
    loadingOn();
    try {
      const res = await api.get(
        `questions/search?limit=${count}&games=${checkedList.map(c => c + 1).join("/-/")}&places=${checkedListPlaces.join("/-/")}&keywords=${keywords.join("/-/")}&to=${to}&from=${from}`
      );
      setElements(res.data, showAnswers, keywords);
    } finally {
      loadingOff();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") searchQuestion();
  };

  // Game type toggles
  const allGames = checkedList.length === gameTypes.length;
  const someGames = checkedList.length > 0 && !allGames;

  const toggleAllGames = (checked: boolean) => {
    setCheckedList(checked ? [0, 1, 2, 3, 4, 5] : []);
  };
  const toggleGame = (idx: number, checked: boolean) => {
    setCheckedList(prev => checked ? [...prev, idx] : prev.filter(i => i !== idx));
  };

  // Search place toggles
  const allPlaces = checkedListPlaces.length === searchPlaces.length;
  const somePlaces = checkedListPlaces.length > 0 && !allPlaces;

  const toggleAllPlaces = (checked: boolean) => {
    setCheckedListPlaces(checked ? [0, 1, 2, 3] : []);
  };
  const togglePlace = (idx: number, checked: boolean) => {
    setCheckedListPlaces(prev => checked ? [...prev, idx] : prev.filter(i => i !== idx));
  };

  return (
    <div className="space-y-6">
      {/* Keyword input */}
      <div>
        <div className="relative">
          <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500 pointer-events-none" />
          <Input
            className="pl-9"
            placeholder={t("search.keyword")}
            value={keyword}
            onChange={e => { setKeyword(e.target.value); setError(null); }}
            onKeyDown={handleKeyDown}
          />
        </div>
        {error && <p className="text-red-500 text-xs mt-1.5">{error}</p>}
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">

        {/* Game types */}
        <div className="space-y-2">
          <CheckItem
            label={t("search.searchInAllTypes")}
            checked={allGames}
            indeterminate={someGames}
            bold
            onChange={toggleAllGames}
          />
          <div className="space-y-1.5 pl-3 border-l-2 border-gray-100 dark:border-gray-800">
            {gameTypes.map((label, idx) => (
              <CheckItem
                key={idx}
                label={label}
                checked={checkedList.includes(idx)}
                onChange={checked => toggleGame(idx, checked)}
              />
            ))}
          </div>
        </div>

        {/* Search places */}
        <div className="space-y-2">
          <CheckItem
            label={t("search.searchInAllParts")}
            checked={allPlaces}
            indeterminate={somePlaces}
            bold
            onChange={toggleAllPlaces}
          />
          <div className="space-y-1.5 pl-3 border-l-2 border-gray-100 dark:border-gray-800">
            {searchPlaces.map((label, idx) => (
              <CheckItem
                key={idx}
                label={label}
                checked={checkedListPlaces.includes(idx)}
                onChange={checked => togglePlace(idx, checked)}
              />
            ))}
          </div>
        </div>

        {/* Count, show answers, date range */}
        <div className="space-y-5">
          <div className="flex gap-6 flex-wrap">
            {/* Result count */}
            <div className="space-y-1.5">
              <label className="text-sm font-semibold text-gray-800 dark:text-gray-100 block">
                {t("search.resultCount")}
              </label>
              <input
                type="number"
                value={count}
                min={10}
                max={120}
                onChange={e => setCount(Math.min(120, Math.max(10, +e.target.value)))}
                className="w-20 h-9 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 text-sm text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Show answers toggle */}
            <div className="space-y-1.5">
              <span className="text-sm font-semibold text-gray-800 dark:text-gray-100 block">
                {t("search.answers")}
              </span>
              <Toggle
                checked={showAnswers}
                labelOn={t("search.show")}
                labelOff={t("search.hide")}
                onChange={() => setShowAnswers(v => !v)}
              />
            </div>
          </div>

          {/* Date range — hidden on mobile */}
          <div className="hidden sm:block space-y-1.5">
            <span className="text-sm font-semibold text-gray-800 dark:text-gray-100 block">
              {t("search.dateRange")}
            </span>
            <div className="flex items-center gap-2">
              <input
                type="date"
                value={from}
                onChange={e => setFrom(e.target.value)}
                className="h-9 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 text-sm text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <span className="text-gray-400 dark:text-gray-500 text-sm">—</span>
              <input
                type="date"
                value={to}
                onChange={e => setTo(e.target.value)}
                className="h-9 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 text-sm text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Search button */}
      <Button onClick={searchQuestion} className="w-full sm:w-72">
        {t("search.searchButton")}
      </Button>
    </div>
  );
};

export default connect(null, { loadingOff, loadingOn })(
  withTranslation()(QuestionSearchComponent)
);
